import {
    EVENT_ADOPTION_METRICS,
    EVENT_ADOPTION_RESULT_LAYOUT,
    buildVisitorAdoptionAggregations,
    parseVisitorAdoptionResults,
    buildVisitorEngagementAggregations,
    parseVisitorEngagementResults,
    buildVisitorRageClicksAggregations,
    parseVisitorRageClicksResults,
    buildEventAdoptionAllUsersIncrease,
    buildEventAdoptionAllUsersDecrease,
    parseEventAdoptionResults,
    EVENT_RAGE_CLICKS_KINDS,
    buildEventRageClicksAggregation,
    parseEventRageClicksResults,
    buildVisitorRetentionAggregations,
    buildAccountRetentionAggregations,
    parseRetentionTrendsResults,
    buildNpsSurveyWow,
    parseNpsSurveyWowResults,
    buildAcquisitionAggregations,
    parseAcquisitionAggregations,
    buildStickinessSignalAggregations,
    parseStickinessSignalResults,
    resolveStickinessMetricParams,
} from '@pendo/middleware';
import { i18n_t as $t } from '@/utils/i18n';
import moment from '@/utils/moment-wrapper';
import momentTimeZone from 'moment-timezone';
import { addCommasToNumber } from '@/utils/formatters';
import { SIGNAL_TYPES } from './signals.constants.js';
import { PERIOD_MAP } from '@/product-engagement-score/aggregations/product-engagement-score-aggregation.utils';
import { getAbbreviation } from '@/product-engagement-score/product-engagement-score.utils';
import {
    buildNpsSignal,
    extractRows,
} from '@/vuex/modules/nps-signal/nps-signal.utils.js';

function getSelectedEventAdoptionKinds (countableIds = []) {
    if (!countableIds.length) {
        return null;
    }

    const { featureIds, pageIds, trackEventIds } = splitCountableIdsByKind(countableIds);
    const selectedKinds = new Set();
    if (featureIds.length) selectedKinds.add('feature');
    if (pageIds.length) selectedKinds.add('page');
    if (trackEventIds.length) selectedKinds.add('trackEvent');

    return selectedKinds;
}

function getFilteredEventAdoptionLayout (countableIds) {
    const selectedKinds = getSelectedEventAdoptionKinds(countableIds);
    if (!selectedKinds) {
        return EVENT_ADOPTION_RESULT_LAYOUT;
    }

    return EVENT_ADOPTION_RESULT_LAYOUT.filter(({ kind }) => selectedKinds.has(kind));
}

function getFilteredEventRageClicksKinds (countableIds = []) {
    if (!countableIds.length) {
        return EVENT_RAGE_CLICKS_KINDS;
    }

    const { featureIds, pageIds } = splitCountableIdsByKind(countableIds);
    const selectedKinds = new Set();
    if (featureIds.length) selectedKinds.add('feature');
    if (pageIds.length) selectedKinds.add('page');

    return EVENT_RAGE_CLICKS_KINDS.filter((kind) => selectedKinds.has(kind));
}

export function buildAggregations (signals, context) {
    const aggregations = [];
    const breakpoints = [];

    signals.forEach((signal) => {
        const aggs = buildAggregationsForSignal(signal, context);
        aggregations.push(...aggs);
        breakpoints.push(aggregations.length);
    });

    return [aggregations, breakpoints];
}

function buildAggregationsForSignal (signal, context) {
    switch (signal) {
        case SIGNAL_TYPES.VISITOR_ADOPTION:
            return buildVisitorAdoptionAggs(context);
        case SIGNAL_TYPES.VISITOR_ENGAGEMENT:
            return buildVisitorEngagementAggs(context);
        case SIGNAL_TYPES.VISITOR_RAGE_CLICKS:
            return buildVisitorRageClicksAggs(context);
        case SIGNAL_TYPES.EVENT_ADOPTION:
            return buildEventAdoptionAggs(context);
        case SIGNAL_TYPES.EVENT_RAGE_CLICKS:
            return buildEventRageClicksAggs(context);
        case SIGNAL_TYPES.VISITOR_RETENTION:
            return buildRetentionAggs(context, 'visitor');
        case SIGNAL_TYPES.ACCOUNT_RETENTION:
            return buildRetentionAggs(context, 'account');
        case SIGNAL_TYPES.NPS_WEEK_OVER_WEEK:
            return buildNpsAggs(context);
        case SIGNAL_TYPES.VISITOR_ACQUISITION:
            return buildAcquisitionAggs(context);
        case SIGNAL_TYPES.STICKINESS:
            return buildStickinessAggs(context);
        default:
            throw new Error(`Unknown signal type: ${signal}`);
    }
}

export function parseAggregationResults (signals, breakpoints, rawData, context = {}) {
    const extractedData = {};
    let previousBreakpoint = 0;

    signals.forEach((signal, index) => {
        const relevantData = rawData.slice(previousBreakpoint, breakpoints[index]);
        previousBreakpoint = breakpoints[index];
        extractedData[signal] = parseAggregationResultsForSignal(signal, relevantData, context);
    });

    return extractedData;
}

function parseAggregationResultsForSignal (signal, relevantData, context) {
    switch (signal) {
        case SIGNAL_TYPES.VISITOR_ADOPTION:
            return parseVisitorAdoptionResults(relevantData);
        case SIGNAL_TYPES.VISITOR_ENGAGEMENT:
            return parseVisitorEngagementResults(relevantData);
        case SIGNAL_TYPES.VISITOR_RAGE_CLICKS:
            return parseVisitorRageClicksResults(relevantData);
        case SIGNAL_TYPES.EVENT_ADOPTION:
            return parseEventAdoptionResults(getFilteredEventAdoptionLayout(context.countableIds), relevantData);
        case SIGNAL_TYPES.EVENT_RAGE_CLICKS:
            return parseEventRageClicksResults(getFilteredEventRageClicksKinds(context.countableIds), relevantData);
        case SIGNAL_TYPES.VISITOR_RETENTION:
            return parseRetentionRawForKind(relevantData, 'visitor');
        case SIGNAL_TYPES.ACCOUNT_RETENTION:
            return parseRetentionRawForKind(relevantData, 'account');
        case SIGNAL_TYPES.NPS_WEEK_OVER_WEEK:
            return parseNpsResults(relevantData);
        case SIGNAL_TYPES.VISITOR_ACQUISITION:
            return parseAcquisitionResults(relevantData, context);
        case SIGNAL_TYPES.STICKINESS:
            return parseStickinessSignalResults(relevantData);
        default:
            throw new Error(`Unknown signal type: ${signal}`);
    }
}

export function createSignals (signalsToFetch, extractedData, context) {
    const signals = [];

    signalsToFetch.forEach((signal) => {
        const dataPoints = extractedData[signal];
        const created = createSignal(signal, dataPoints, context);
        if (Array.isArray(created)) {
            signals.push(...created);
        } else if (created) {
            signals.push(created);
        }
    });

    return signals;
}

function createSignal (signal, dataPoints, context) {
    switch (signal) {
        case SIGNAL_TYPES.VISITOR_ADOPTION:
            return createVisitorAdoptionSignal(dataPoints, context);
        case SIGNAL_TYPES.VISITOR_ENGAGEMENT:
            return createVisitorEngagementSignal(dataPoints, context);
        case SIGNAL_TYPES.VISITOR_RAGE_CLICKS:
            return createVisitorRageClicksSignal(dataPoints, context);
        case SIGNAL_TYPES.EVENT_ADOPTION:
            return createEventAdoptionSignals(dataPoints, context);
        case SIGNAL_TYPES.EVENT_RAGE_CLICKS:
            return createEventRageClicksSignal(dataPoints, context);
        case SIGNAL_TYPES.VISITOR_RETENTION:
            return createRetentionSignals(dataPoints, context, SIGNAL_TYPES.VISITOR_RETENTION, 'visitor');
        case SIGNAL_TYPES.ACCOUNT_RETENTION:
            return createRetentionSignals(dataPoints, context, SIGNAL_TYPES.ACCOUNT_RETENTION, 'account');
        case SIGNAL_TYPES.NPS_WEEK_OVER_WEEK:
            return createNpsSignal(dataPoints, context);
        case SIGNAL_TYPES.VISITOR_ACQUISITION:
            return createAcquisitionSignal(dataPoints, context);
        case SIGNAL_TYPES.STICKINESS:
            return createStickinessSignal(dataPoints, context);
        default:
            throw new Error(`Unknown signal type: ${signal}`);
    }
}

// --- stickiness (single-app) ------------------------------------------------

const STICKINESS_USER_BASE_TO_KIND = { visitors: 'visitor', accounts: 'account' };

// Default metric shape when no (or partial/invalid) PES config: DAU/MAU over visitors.
const STICKINESS_DEFAULT_NUMERATOR = 'daily';
const STICKINESS_DEFAULT_DENOMINATOR = 'monthly';
const STICKINESS_DEFAULT_USER_BASE = 'visitors';

// The metric-shape label shown on the card (e.g. "DAU/MAU", "WAU/MAU", "WAA/MAA"),
// derived from the resolved PES config so it matches the numbers. Falls back per
// field to DAU/MAU over visitors when the config is missing or has unknown values.
export function buildStickinessMetricLabel (stickinessConfig) {
    const numerator = PERIOD_MAP[stickinessConfig?.numerator] ? stickinessConfig.numerator : STICKINESS_DEFAULT_NUMERATOR;
    const denominator = PERIOD_MAP[stickinessConfig?.denominator] ? stickinessConfig.denominator : STICKINESS_DEFAULT_DENOMINATOR;
    const userBase = STICKINESS_USER_BASE_TO_KIND[stickinessConfig?.userBase] ? stickinessConfig.userBase : STICKINESS_DEFAULT_USER_BASE;

    return `${getAbbreviation(numerator, userBase)}/${getAbbreviation(denominator, userBase)}`.toUpperCase();
}

function buildStickinessAggs ({ appId, hasShowAnonymousFlag, segmentId, stickinessConfig }) {
    const { current, previous } = buildStickinessSignalAggregations({
        appId,
        hasShowAnonymousFlag,
        segmentId,
        ...resolveStickinessMetricParams(stickinessConfig),
    });

    return [current, previous].map(({ name, productArea, subProductArea, pipeline }) => ({
        name,
        productArea,
        subProductArea,
        responseFormat: 'rows',
        build: () => pipeline,
    }));
}

function createStickinessSignal (dataPoints, context = {}) {
    const [currentEnd, currentStart, previousEnd, previousStart] = get7DayPeriodTimestamps();
    const currentPeriodLabel = formatWeekLabel(currentStart, currentEnd);
    const previousPeriodLabel = formatWeekLabel(previousStart, previousEnd);
    const slackPeriodLabel = formatDateRangeForSlack(previousStart, currentEnd);

    const { appId, tagName, delta, currentRaw, previousRaw } = dataPoints;
    const { appData, stickinessConfig } = context;
    const kind = STICKINESS_USER_BASE_TO_KIND[stickinessConfig?.userBase] ?? 'visitor';
    // The metric-shape acronym (e.g. DAU/MAU, WAU/MAU). Different locales place it
    // in the before- or after-tag copy, so pass it to both.
    const metric = buildStickinessMetricLabel(stickinessConfig);

    return {
        type: SIGNAL_TYPES.STICKINESS,
        kind,
        title: $t('signals.Stickiness'),

        // App name is baked into the agg output (self-describing); fall back to
        // the client app data only if the agg didn't supply it.
        appId: appId ?? appData?.appId,
        tagName: tagName ?? appData?.name,
        showAppTag: true,
        appIcon: buildAppIcon(appData?.icon),
        segmentId: context.segmentId,

        delta,
        previousPeriodHadNoData: previousRaw === 0 && currentRaw > 0,
        previousDataPoint: previousRaw,
        currentDataPoint: currentRaw,

        currentPeriodLabel,
        previousPeriodLabel,
        slackPeriodLabel,

        textBeforeTagName: $t('signals.stickinessTextBefore', { metric }),
        textAfterTagName: buildStickinessTextAfter({ delta, currentPeriodLabel, previousRaw, currentRaw, metric }),
        stickinessConfig: stickinessConfig || null,
    };
}

function buildStickinessTextAfter ({ delta, currentPeriodLabel, previousRaw, currentRaw, metric }) {
    if ((previousRaw === 0 && currentRaw > 0) || delta === null) {
        return $t('signals.stickinessTextAfterNewBaseline', {
            currentPeriod: currentPeriodLabel,
            currentRate: currentRaw,
            metric,
        });
    }

    if (delta === 0) {
        return $t('signals.stickinessTextAfterUnchanged', {
            currentPeriod: currentPeriodLabel,
            currentRate: currentRaw,
            previousRate: previousRaw,
            metric,
        });
    }

    const key = delta > 0 ? 'signals.stickinessTextAfterIncrease' : 'signals.stickinessTextAfterDecrease';

    return $t(key, {
        currentPeriod: currentPeriodLabel,
        currentRate: currentRaw,
        previousRate: previousRaw,
        delta: Math.abs(delta),
        metric,
    });
}

// --- shared date helpers -----------------------------------------------------

function formatWeekLabel (start, end) {
    return start.isSame(end, 'month')
        ? $t('signals.weekDateRange', { start: start.format('MMMM D'), end: end.format('D') })
        : $t('signals.weekDateRange', { start: start.format('MMMM D'), end: end.format('MMMM D') });
}

function formatDateRangeForSlack (start, end) {
    return `${start.format('MMMM D')} - ${end.format('MMMM D')}`;
}

function get7DayPeriodTimestamps () {
    function getTimestampForDayOffset (offset) { return moment(undefined, { ignoreTimeZone: true }).subtract(offset, 'days'); }

    return [getTimestampForDayOffset(1), getTimestampForDayOffset(7), getTimestampForDayOffset(8), getTimestampForDayOffset(14)];
}

function computeLast7DaysRange () {
    const end = moment(undefined, { ignoreTimeZone: true }).subtract(1, 'days');
    const start = end.clone().subtract(6, 'days');

    return {
        timeRangeStart: start.format('MMMM D'),
        timeRangeEnd: end.format('MMMM D'),
    };
}

function computeRetentionPeriodStart (timeZone) {
    const currentWeekStart = timeZone
        ? momentTimeZone.tz(timeZone).startOf('isoWeek')
        : moment(undefined, { ignoreTimeZone: true }).startOf('isoWeek');

    return currentWeekStart.clone().subtract(7, 'weeks').valueOf();
}

function computeRetentionAnalyticsRange (timeZone) {
    const currentWeekStart = timeZone
        ? momentTimeZone.tz(timeZone).startOf('isoWeek')
        : moment(undefined, { ignoreTimeZone: true }).startOf('isoWeek');

    return {
        timeRangeStart: currentWeekStart.clone().subtract(3, 'weeks').startOf('isoWeek').format('MMMM D'),
        timeRangeEnd: currentWeekStart.clone().subtract(2, 'weeks').endOf('isoWeek').format('MMMM D'),
    };
}

// --- countable helpers -------------------------------------------------------

function splitCountableIdsByKind (countableIds = []) {
    const featureIds = [];
    const pageIds = [];
    const trackEventIds = [];

    countableIds.forEach((countable) => {
        if (!countable?.id || !countable?.kind) {
            return;
        }
        if (countable.kind === 'Feature') {
            featureIds.push(countable.id);
        } else if (countable.kind === 'Page') {
            pageIds.push(countable.id);
        } else if (countable.kind === 'TrackType') {
            trackEventIds.push(countable.id);
        }
    });

    return { featureIds, pageIds, trackEventIds };
}

function buildAppIcon (rawIcon) {
    if (typeof rawIcon === 'string') {
        return { type: rawIcon, size: 12 };
    }
    if (rawIcon) {
        return { ...rawIcon, size: 12 };
    }

    return null;
}

function getCountableIcon (countableKind) {
    const kindMap = {
        feature: 'mouse-pointer',
        page: 'file',
        tracktype: 'zap',
    };
    const type = kindMap[countableKind?.toLowerCase()] || 'feature';

    return { type, size: 12 };
}

function getCountableTitle (countableKind) {
    const kindTitleMap = {
        feature: $t('trends.Feature adoption'),
        page: $t('trends.Page adoption'),
        tracktype: $t('trends.Track event adoption'),
    };

    return kindTitleMap[countableKind?.toLowerCase()] || $t('trends.Adoption');
}

// --- visitor adoption (single-app) ------------------------------------------

function buildVisitorAdoptionAggs ({ appId, hasShowAnonymousFlag, segmentId }) {
    const { iterativeAggregations } = buildVisitorAdoptionAggregations({
        appId,
        hasShowAnonymousFlag,
        segmentId,
    });

    return iterativeAggregations.map(({ agg: { name, pipeline, productArea, subProductArea } }) => ({
        name,
        productArea,
        subProductArea,
        responseFormat: 'rows',
        build: () => pipeline,
    }));
}

function createVisitorAdoptionSignal (dataPoints, context = {}) {
    const [currentEnd, currentStart, previousEnd, previousStart] = get7DayPeriodTimestamps();
    const currentPeriodLabel = formatWeekLabel(currentStart, currentEnd);
    const previousPeriodLabel = formatWeekLabel(previousStart, previousEnd);
    const slackPeriodLabel = formatDateRangeForSlack(previousStart, currentEnd);

    const { appId, tagName, delta, previousDataPoint, currentDataPoint } = dataPoints;

    return {
        type: SIGNAL_TYPES.VISITOR_ADOPTION,
        kind: 'visitor',
        title: $t('signals.Visitor_adoption'),

        appId,
        tagName,
        showAppTag: true,
        segmentId: context.segmentId,

        delta,
        previousPeriodHadNoData: previousDataPoint === 0 && currentDataPoint > 0,
        previousDataPoint,
        currentDataPoint,

        currentPeriodLabel,
        previousPeriodLabel,
        slackPeriodLabel,

        textBeforeTagName: $t('signals.visitorAdoptionTextBefore'),
        textAfterTagName: buildVisitorAdoptionTextAfter({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }),
    };
}

function buildVisitorAdoptionTextAfter ({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }) {
    const currentCount = addCommasToNumber(currentDataPoint);

    if ((previousDataPoint === 0 && currentDataPoint > 0) || delta === null) {
        return $t('signals.visitorAdoptionTextAfterNewBaseline', {
            currentPeriod: currentPeriodLabel,
            currentCount,
        });
    }

    const previousCount = addCommasToNumber(previousDataPoint);

    if (delta === 0) {
        return $t('signals.visitorAdoptionTextAfterUnchanged', {
            currentPeriod: currentPeriodLabel,
            currentCount,
            previousCount,
        });
    }

    const key = delta > 0 ? 'signals.visitorAdoptionTextAfterIncrease' : 'signals.visitorAdoptionTextAfterDecrease';

    return $t(key, {
        currentPeriod: currentPeriodLabel,
        currentCount,
        previousCount,
        delta: addCommasToNumber(Math.abs(delta)),
    });
}

// --- visitor engagement -----------------------------------------------------

function buildVisitorEngagementAggs ({ appId, hasShowAnonymousFlag, segmentId }) {
    const { iterativeAggregations } = buildVisitorEngagementAggregations({
        appId,
        hasShowAnonymousFlag,
        segmentId,
    });

    return iterativeAggregations.map(({ agg: { name, pipeline, productArea, subProductArea } }) => ({
        name,
        productArea,
        subProductArea,
        responseFormat: 'rows',
        build: () => pipeline,
    }));
}

function createVisitorEngagementSignal (dataPoints, context = {}) {
    const [currentEnd, currentStart, previousEnd, previousStart] = get7DayPeriodTimestamps();
    const currentPeriodLabel = formatWeekLabel(currentStart, currentEnd);
    const previousPeriodLabel = formatWeekLabel(previousStart, previousEnd);
    const slackPeriodLabel = formatDateRangeForSlack(previousStart, currentEnd);

    const { appId, tagName, delta, previousDataPoint, currentDataPoint } = dataPoints;

    const previousMinutes = previousDataPoint.minutes;
    const previousSeconds = previousDataPoint.seconds;
    const currentMinutes = currentDataPoint.minutes;
    const currentSeconds = currentDataPoint.seconds;

    return {
        type: SIGNAL_TYPES.VISITOR_ENGAGEMENT,
        kind: 'visitor',
        title: $t('signals.Visitor_engagement'),

        appId,
        tagName,
        showAppTag: true,
        segmentId: context.segmentId,

        delta,
        previousPeriodHadNoData: previousMinutes + previousSeconds === 0 && currentMinutes + currentSeconds > 0,
        previousDataPoint: `${previousMinutes}m ${previousSeconds}s`,
        currentDataPoint: `${currentMinutes}m ${currentSeconds}s`,

        currentPeriodLabel,
        previousPeriodLabel,
        slackPeriodLabel,

        textBeforeTagName: $t('signals.visitorEngagementTextBefore'),
        textAfterTagName: buildVisitorEngagementTextAfter({ delta, currentPeriodLabel, previousMinutes, previousSeconds, currentMinutes, currentSeconds }),
    };
}

function buildVisitorEngagementTextAfter ({ delta, currentPeriodLabel, previousMinutes, previousSeconds, currentMinutes, currentSeconds }) {
    if ((previousMinutes + previousSeconds === 0 && currentMinutes + currentSeconds > 0) || delta === null) {
        return $t('signals.visitorEngagementTextAfterNewBaseline', {
            currentPeriod: currentPeriodLabel,
            currentMinutes,
            currentSeconds,
        });
    }

    if (delta === 0) {
        return $t('signals.visitorEngagementTextAfterUnchanged', {
            currentPeriod: currentPeriodLabel,
            currentMinutes,
            currentSeconds,
            previousMinutes,
            previousSeconds,
        });
    }

    const key = delta > 0 ? 'signals.visitorEngagementTextAfterIncrease' : 'signals.visitorEngagementTextAfterDecrease';

    return $t(key, {
        currentPeriod: currentPeriodLabel,
        currentMinutes,
        currentSeconds,
        previousMinutes,
        previousSeconds,
        delta: addCommasToNumber(Math.abs(delta)),
    });
}

// functions specific to visitor rage clicks

function buildVisitorRageClicksAggs ({ appId, hasShowAnonymousFlag, segmentId }) {
    const { iterativeAggregations } = buildVisitorRageClicksAggregations({
        appId,
        hasShowAnonymousFlag,
        segmentId,
    });

    return iterativeAggregations.map(({ agg: { name, pipeline, productArea, subProductArea } }) => ({
        name,
        productArea,
        subProductArea,
        responseFormat: 'rows',
        build: () => pipeline,
    }));
}

function createVisitorRageClicksSignal (dataPoints, context = {}) {

    const [currentEnd, currentStart, previousEnd, previousStart] = get7DayPeriodTimestamps();
    const currentPeriodLabel = formatWeekLabel(currentStart, currentEnd);
    const previousPeriodLabel = formatWeekLabel(previousStart, previousEnd);
    const slackPeriodLabel = formatDateRangeForSlack(previousStart, currentEnd);

    const { appId, tagName, delta, previousDataPoint, currentDataPoint } = dataPoints;

    return {
        type: SIGNAL_TYPES.VISITOR_RAGE_CLICKS,
        kind: 'visitor',
        title: $t('signals.Visitor_frustration'),
        headlineLabel: $t('signals.rageClicksHeadlineLabel'),
        isNegativeMetric: true,

        appId,
        tagName,
        showAppTag: true,
        segmentId: context.segmentId,

        delta,
        previousPeriodHadNoData: previousDataPoint === 0 && currentDataPoint > 0,
        previousDataPoint,
        currentDataPoint,

        currentPeriodLabel,
        previousPeriodLabel,
        slackPeriodLabel,

        textBeforeTagName: $t('signals.visitorRageClicksTextBefore'),
        textAfterTagName: buildVisitorRageClicksTextAfter({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }),
    };
}

function buildVisitorRageClicksTextAfter ({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }) {
    const currentCount = addCommasToNumber(currentDataPoint);

    if ((previousDataPoint === 0 && currentDataPoint > 0) || delta === null) {
        return $t('signals.visitorRageClicksTextAfterNewBaseline', {
            currentPeriod: currentPeriodLabel,
            currentCount,
        });
    }

    const previousCount = addCommasToNumber(previousDataPoint);

    if (delta === 0) {
        return $t('signals.visitorRageClicksTextAfterUnchanged', {
            currentPeriod: currentPeriodLabel,
            currentCount,
            previousCount,
        });
    }

    const key = delta > 0 ? 'signals.visitorRageClicksTextAfterIncrease' : 'signals.visitorRageClicksTextAfterDecrease';

    return $t(key, {
        currentPeriod: currentPeriodLabel,
        currentCount,
        previousCount,
        delta: addCommasToNumber(Math.abs(delta)),
    });
}

// --- event rage clicks (feature-level) -----------------------------------------------

function buildEventRageClicksAggs ({ appId, hasShowAnonymousFlag, countableIds = [], segmentId }) {
    const { featureIds, pageIds } = splitCountableIdsByKind(countableIds);
    const kinds = getFilteredEventRageClicksKinds(countableIds);

    if (kinds.length === 0) {
        return [];
    }

    const options = {
        appId,
        hasShowAnonymousFlag,
        featureIds: featureIds.length ? featureIds : undefined,
        pageIds: pageIds.length ? pageIds : undefined,
        segmentId,
    };

    return kinds.map((kind) => {
        const { name, pipeline, productArea, subProductArea } = buildEventRageClicksAggregation(options, kind);

        return {
            kind,
            name: `${name}-${kind}`,
            productArea,
            subProductArea,
            responseFormat: 'rows',
            build: () => pipeline,
        };
    });
}

function getEventRageClicksTitle (normalizedKind) {
    const titleMap = {
        Feature: $t('signals.Feature_frustration'),
        Page: $t('signals.Page_frustration'),
    };

    return titleMap[normalizedKind] || $t('signals.Feature_frustration');
}

function createEventRageClicksSignal (parsedResult, context = {}) {
    if (!parsedResult) return null;
    if (parsedResult.delta === 0) return null;

    const { countableId, countableName, countableKind, delta, previousDataPoint, currentDataPoint } = parsedResult;
    const { appData, countableIds = [] } = context;
    const [currentEnd, currentStart, previousEnd, previousStart] = get7DayPeriodTimestamps();
    const currentPeriodLabel = formatWeekLabel(currentStart, currentEnd);
    const previousPeriodLabel = formatWeekLabel(previousStart, previousEnd);
    const slackPeriodLabel = formatDateRangeForSlack(previousStart, currentEnd);
    const { featureIds: selectedFeatureIds, pageIds: selectedPageIds } = splitCountableIdsByKind(countableIds);
    const hasSelectedEvents = selectedFeatureIds.length > 0 || selectedPageIds.length > 0;

    // Capitalize to match the 'Feature'/'Page'/'TrackType' convention used throughout the UI.
    const normalizedKind = countableKind.charAt(0).toUpperCase() + countableKind.slice(1);

    return {
        type: SIGNAL_TYPES.EVENT_RAGE_CLICKS,
        kind: normalizedKind,
        title: getEventRageClicksTitle(normalizedKind),
        headlineLabel: $t('signals.eventRageClicksHeadlineLabel', { eventName: countableName }),
        isNegativeMetric: true,
        tagName: countableName,
        tagIcon: getCountableIcon(countableKind),
        appName: appData?.name,
        appIcon: buildAppIcon(appData?.icon),
        showAppTag: true,
        appId: appData?.appId,
        countableId,
        countableKind: normalizedKind,
        segmentId: context.segmentId,
        delta,
        previousPeriodHadNoData: delta === null,
        previousDataPoint,
        currentDataPoint,
        currentPeriodLabel,
        previousPeriodLabel,
        slackPeriodLabel,
        textBeforeTagName: $t('signals.eventRageClicksTextBefore', { currentDataPoint: addCommasToNumber(currentDataPoint) }),
        textAfterTagName: buildEventRageClicksTextAfter({ delta, previousDataPoint, currentPeriodLabel, hasSelectedEvents }),
    };
}

function buildEventRageClicksTextAfter ({ delta, previousDataPoint, currentPeriodLabel, hasSelectedEvents }) {
    if (delta === null) {
        const key = hasSelectedEvents
            ? 'signals.eventRageClicksTextAfterNewBaselineSelectedEvents'
            : 'signals.eventRageClicksTextAfterNewBaselineApp';

        return $t(key, { currentPeriod: currentPeriodLabel });
    }

    const key = hasSelectedEvents
        ? 'signals.eventRageClicksTextAfterSelectedEvents'
        : 'signals.eventRageClicksTextAfterApp';

    return $t(key, {
        currentPeriod: currentPeriodLabel,
        previousDataPoint: addCommasToNumber(previousDataPoint),
        delta: Math.abs(delta),
    });
}

// --- feature/page/track event adoption --------------------------------------------------

const EVENT_ADOPTION_METRIC_BUILDERS = {
    [EVENT_ADOPTION_METRICS.ALL_USERS_INCREASE]: buildEventAdoptionAllUsersIncrease,
    [EVENT_ADOPTION_METRICS.ALL_USERS_DECREASE]: buildEventAdoptionAllUsersDecrease,
};

function buildEventAdoptionAggs ({ appId, hasShowAnonymousFlag, countableIds = [], segmentId }) {
    const { featureIds, pageIds, trackEventIds } = splitCountableIdsByKind(countableIds);
    const options = {
        appId,
        hasShowAnonymousFlag,
        featureIds: featureIds.length ? featureIds : undefined,
        pageIds: pageIds.length ? pageIds : undefined,
        trackEventIds: trackEventIds.length ? trackEventIds : undefined,
        segmentId,
    };

    return getFilteredEventAdoptionLayout(countableIds).map(({ metric, kind }) => {
        const { name, pipeline, productArea, subProductArea } = EVENT_ADOPTION_METRIC_BUILDERS[metric](options, kind);

        return {
            metric,
            kind,
            name: `${name}-${kind}`,
            productArea,
            subProductArea,
            responseFormat: 'rows',
            build: () => pipeline,
        };
    });
}

function createEventAdoptionSignals (parsedResults, context) {
    const { appData, countableIds = [] } = context;
    const hasSelectedEvents = countableIds.length > 0;
    const { timeRangeStart, timeRangeEnd } = computeLast7DaysRange();

    return parsedResults.map((parsedResult) => buildEventAdoptionSignal(parsedResult, appData, hasSelectedEvents, { timeRangeStart, timeRangeEnd }));
}

function buildEventAdoptionSignal (parsedResult, appData, hasSelectedEvents, timeRange) {
    const { metric, countableKind, countableId, countableName, adoptionRateCurrent, adoptionRatePrevious, delta, trendDirection } = parsedResult;
    const { textBeforeTagName, textAfterTagName } = buildEventAdoptionText(parsedResult, appData, hasSelectedEvents);
    const appIcon = buildAppIcon(appData?.icon);
    const tagIcon = getCountableIcon(countableKind);

    return {
        type: SIGNAL_TYPES.EVENT_ADOPTION,
        kind: countableKind,
        title: getCountableTitle(countableKind),
        textBeforeTagName,
        textAfterTagName,
        showAppTag: true,
        tagName: countableName,
        tagIcon,
        appName: appData?.name,
        appIcon,
        delta,
        trendDirection,
        adoptionRateCurrent,
        adoptionRatePrevious,
        countableId,
        countableKind,
        metric,
        appId: appData?.appId,
        timeRangeStart: timeRange.timeRangeStart,
        timeRangeEnd: timeRange.timeRangeEnd,
        slackPeriodLabel: `${timeRange.timeRangeStart} - ${timeRange.timeRangeEnd}`,
    };
}

function buildEventAdoptionText (parsedResult, appData, hasSelectedEvents) {
    const { metric, adoptionRateCurrent, adoptionRatePrevious, delta } = parsedResult;
    const currentRate = adoptionRateCurrent ?? 0;
    const previousRate = adoptionRatePrevious ?? 0;
    const absChange = delta != null ? Math.abs(delta) : 0;

    if (metric === EVENT_ADOPTION_METRICS.ALL_USERS_INCREASE) {
        return {
            textBeforeTagName: $t('signals.eventAdoptionTextBefore'),
            textAfterTagName: hasSelectedEvents
                ? $t('signals.eventAdoptionIncreaseTextAfterSelectedEvents', { absChange })
                : $t('signals.eventAdoptionIncreaseTextAfterApp', { absChange }),
        };
    }

    return {
        textBeforeTagName: $t('signals.eventAdoptionTextBefore'),
        textAfterTagName: hasSelectedEvents
            ? $t('signals.eventAdoptionDecreaseTextAfterSelectedEvents', { absChange, previousRate, currentRate })
            : $t('signals.eventAdoptionDecreaseTextAfterApp', { absChange, previousRate, currentRate }),
    };
}

// --- retention (visitor / account) ------------------------------------------

const RETENTION_PIPELINE_NAMES = ['newUsers', 'allUsers'];

function buildRetentionAggs ({ appId, timeZone, segmentId }, kind) {
    const startTimestamp = computeRetentionPeriodStart(timeZone);
    const builder = kind === 'account' ? buildAccountRetentionAggregations : buildVisitorRetentionAggregations;
    const { newUsers, allUsers } = builder({
        appId,
        hasShowAnonymousFlag: false,
        startTimestamp,
        segmentId,
    });

    return RETENTION_PIPELINE_NAMES.map((pipelineName) => {
        const pipelineDef = pipelineName === 'newUsers' ? newUsers : allUsers;

        return {
            name: pipelineDef.name,
            productArea: pipelineDef.productArea,
            subProductArea: pipelineDef.subProductArea,
            responseFormat: 'rows',
            build: () => pipelineDef.pipeline,
        };
    });
}

function parseRetentionRawForKind (relevantData, kind) {
    const [newUsersRows = [], allUsersRows = []] = relevantData;

    return { kind, newUsersRows, allUsersRows };
}

function formatCohortWeekLabel (epochMs) {
    const start = momentTimeZone.utc(epochMs);
    const end = momentTimeZone.utc(epochMs).add(6, 'days');

    return start.isSame(end, 'month')
        ? $t('signals.weekDateRange', { start: start.format('MMMM D'), end: end.format('D') })
        : $t('signals.weekDateRange', { start: start.format('MMMM D'), end: end.format('MMMM D') });
}

export function buildRetentionCopy (result) {
    const {
        kind,
        current_retention: currentPct,
        previous_retention: previousPct,
        delta,
        current_week_start: currentWeekStart,
        previous_week_start: previousWeekStart,
    } = result;

    const currentPeriodLabel = formatCohortWeekLabel(currentWeekStart);
    const previousPeriodLabel = formatCohortWeekLabel(previousWeekStart);

    const textBeforeKey = kind === 'account' ? 'signals.retentionAccountTextBefore' : 'signals.retentionVisitorTextBefore';
    const textBefore = $t(textBeforeKey, { currentPct });

    let textAfter;
    if (delta === null || delta === undefined) {
        textAfter = $t('signals.retentionTextAfterNoPct', {
            currentPeriod: currentPeriodLabel,
            previousPct,
            previousPeriod: previousPeriodLabel,
        });
    } else if (delta === 0) {
        textAfter = $t('signals.retentionTextAfterUnchanged', { currentPeriod: currentPeriodLabel });
    } else {
        const key = delta > 0 ? 'signals.retentionTextAfterIncrease' : 'signals.retentionTextAfterDecrease';
        textAfter = $t(key, { currentPeriod: currentPeriodLabel, delta: Math.abs(delta) });
    }

    return { textBefore, textAfter, currentPeriodLabel, previousPeriodLabel };
}

function createRetentionSignals (kindInput, context, signalType, kind) {
    const { appId, appData, timeZone, segmentId } = context;
    const parsed = parseRetentionTrendsResults({ appId, inputs: [kindInput] });
    const results = parsed?.data?.results ?? [];
    const { timeRangeStart, timeRangeEnd } = computeRetentionAnalyticsRange(timeZone);
    const tagName = appData?.name;
    const appIcon = buildAppIcon(appData?.icon);

    return results
        .filter((result) => result.metric === 'week_over_week')
        .map((result) => {
            const {
                current_retention: newUsersPercentage,
                previous_retention: existingUsersPercentage,
                delta,
            } = result;
            const { textBefore, textAfter, currentPeriodLabel, previousPeriodLabel } = buildRetentionCopy(result);
            const title = kind === 'account' ? $t('trends.Account retention') : $t('trends.Visitor retention');

            return {
                type: signalType,
                kind,
                title,
                newUsersPercentage,
                existingUsersPercentage,
                delta,
                currentPeriodLabel,
                previousPeriodLabel,
                textBeforeTagName: textBefore,
                textAfterTagName: textAfter,
                showAppTag: true,
                tagName,
                appIcon,
                appId: appData?.appId,
                segmentId,
                timeRangeStart,
                timeRangeEnd,
                slackPeriodLabel: `${timeRangeStart} - ${timeRangeEnd}`,
            };
        });
}

// --- NPS week-over-week ----------------------------------------------------

function wrapAsAggConnect (pipelineSpec) {
    return {
        name: pipelineSpec.name,
        productArea: pipelineSpec.productArea,
        subProductArea: pipelineSpec.subProductArea,
        responseFormat: 'rows',
        build: () => pipelineSpec.pipeline,
    };
}

function buildNpsAggs (context) {
    if (!context.npsSurvey) return [];

    const { current, previous } = buildNpsSurveyWow(context.npsSurvey.mwSettings);

    return [wrapAsAggConnect(current), wrapAsAggConnect(previous)];
}

function parseNpsResults (relevantData) {
    if (relevantData.length === 0) return null;

    const [currentRaw, previousRaw] = relevantData;

    return parseNpsSurveyWowResults({
        currentRows: extractRows(currentRaw),
        previousRows: extractRows(previousRaw),
    });
}

function createNpsSignal (parsed, context) {
    if (!parsed || !context.npsSurvey) return null;

    return buildNpsSignal(parsed, {
        appData: context.npsSurvey.appData,
        periodLabels: context.npsSurvey.periodLabels,
        segmentId: context.segmentId,
        surveyName: context.npsSurvey.surveyName,
        guideId: context.npsSurvey.mwSettings?.guideId,
        pollId: context.npsSurvey.mwSettings?.pollId,
        asDecimal: !!context.npsAsDecimal,
    });
}

// --- visitor acquisition ----------------------------------------------------

function buildAcquisitionAggs (context) {
    const agg = buildAcquisitionAggregations({
        appId: context.appId,
        hasShowAnonymousFlag: context.hasShowAnonymousFlag,
        segmentId: context.segmentId,
        subscriptionFlags: context.subscriptionFlags,
        kind: 'visitor',
    });

    if (!agg || 'invalidOperator' in agg) return [];

    return [{
        name: agg.name,
        productArea: agg.productArea,
        subProductArea: agg.subProductArea,
        build: () => agg.pipeline,
    }];
}

function parseAcquisitionResults (relevantData, context) {
    if (relevantData.length === 0 || !relevantData[0]) return null;
    try {
        return parseAcquisitionAggregations(relevantData[0], context.appData?.appId, context.appData?.name);
    } catch {
        return null;
    }
}

function createAcquisitionSignal (parsed, context = {}) {
    if (!parsed) return null;

    const { delta, previousDataPoint, currentDataPoint } = parsed;
    const [currentEnd, currentStart, previousEnd, previousStart] = get7DayPeriodTimestamps();
    const currentPeriodLabel = formatWeekLabel(currentStart, currentEnd);
    const previousPeriodLabel = formatWeekLabel(previousStart, previousEnd);
    const slackPeriodLabel = formatDateRangeForSlack(previousStart, currentEnd);

    return {
        type: SIGNAL_TYPES.VISITOR_ACQUISITION,
        kind: 'visitor',
        title: $t('signals.Visitor_acquisition'),
        headlineLabel: $t('signals.visitorAcquisitionHeadlineLabel'),
        segmentId: context.segmentId,
        appId: context.appData?.appId,
        tagName: context.appData?.name,
        showAppTag: true,
        delta,
        previousPeriodHadNoData: previousDataPoint === 0 && currentDataPoint > 0,
        previousDataPoint,
        currentDataPoint,
        currentPeriodLabel,
        previousPeriodLabel,
        slackPeriodLabel,
        textBeforeTagName: $t('signals.visitorAcquisitionTextBefore'),
        textAfterTagName: buildAcquisitionTextAfter({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }),
    };
}

function buildAcquisitionTextAfter ({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }) {
    const currentCountStr = addCommasToNumber(currentDataPoint);

    if ((previousDataPoint === 0 && currentDataPoint > 0) || delta === null) {
        return $t('signals.visitorAcquisitionTextAfterNewBaseline', {
            currentPeriod: currentPeriodLabel,
            currentCount: currentCountStr,
        });
    }

    const previousCountStr = addCommasToNumber(previousDataPoint);

    if (delta === 0) {
        return $t('signals.visitorAcquisitionTextAfterUnchanged', {
            currentPeriod: currentPeriodLabel,
            currentCount: currentCountStr,
            previousCount: previousCountStr,
        });
    }

    const key = delta > 0 ? 'signals.visitorAcquisitionTextAfterIncrease' : 'signals.visitorAcquisitionTextAfterDecrease';

    return $t(key, {
        currentPeriod: currentPeriodLabel,
        currentCount: currentCountStr,
        previousCount: previousCountStr,
        delta: delta == null ? null : addCommasToNumber(Math.abs(delta)),
    });
}

// --- context helpers exposed for module use ---------------------------------

export function buildAppDataFromContext (rootGetters, appId) {
    const appDisplayName = rootGetters['trendsSettings/getAppDisplayName'];
    const trendsApp = rootGetters['trendsSettings/getApp'];
    const appIcon = rootGetters['apps/getIconByAppId']?.(appId);

    return {
        name: appDisplayName || trendsApp?.name,
        icon: appIcon,
        appId,
    };
}

export function getSubscriptionTimeZone (rootGetters) {
    const subscription = rootGetters['subscription/getActiveSub'];

    return subscription?.timeZone || null;
}
